<!-- =========================================================================================
  File Name: UserList.vue
  Description: User List page
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>

  <div id="page-facility-list" class="ag-grid-container">
    <statistics-cards :data="statisticalData"/>

    <div class="vx-card">
        <grid-panel
            v-if="showGridOne"
            height="md"
            :column-defs="columnDefs"
            :data="publishedFacilities"
            :auto-size="true"
            :show-deleted-toggle="true"
            :loading="busy"
            @rowClicked="view"
            @update:deleted="updateDeleted"
            title="Facilities"
        >
          <template v-slot:right>
            <button-toggle
                v-model="toggleValue"
                :options="gridToggle"
            />
          </template>
          <vs-dropdown-item v-if="componentAccess.canCreate.access">
            <span
                class="flex items-center"
                @click="$router.push({ name: 'facilities.create' })"
            >
              <feather-icon icon="PlusIcon" svgClasses="h-4 w-4" class="mr-2" />
              <span>Create Facility</span>
            </span>
          </vs-dropdown-item>

        </grid-panel>

      <grid-panel
          v-if="!showGridOne"
          height="md"
          :column-defs="columnDefs"
          :data="unpublishedFacilities"
          :auto-size="true"
          :show-deleted-toggle="true"
          :loading="busy"
          @rowClicked="view"
          @update:deleted="updateDeleted"
          title="Facilities"
      >
        <template v-slot:right>
          <button-toggle
              v-model="toggleValue"
              :options="gridToggle"
          />
        </template>
        <vs-dropdown-item v-if="componentAccess.canCreate.access">
            <span
                class="flex items-center"
                @click="$router.push({ name: 'facilities.create' })"
            >
              <feather-icon icon="PlusIcon" svgClasses="h-4 w-4" class="mr-2" />
              <span>Create Facility</span>
            </span>
        </vs-dropdown-item>

      </grid-panel>
      </div>
  </div>
</template>

<script>

import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";

// Cell Renderer
import CellMatchingMixin from "@/views/pages/sync/CellMatchingMixin";
import GridPanel from "@/views/components/ag-grid/GridPanel";
import CellRendererVerified from "@/views/components/ag-grid/cell-renderer/CellRendererVerified";
import StatBox from "@/views/components/stats/StatBox";
import CellRendererDate from "../../components/ag-grid/cell-renderer/CellRendererDate";
import StatisticsCards from '@/components/statistics-cards/StatisticsCards.vue'
import CanAccessMixin from "@/components/mixins/CanAccessMixin";
import {dateFilterParams} from '../../components/ag-grid/filter-params/DateComparator';
import ButtonToggle from "../../components/extra-components/toggle/ButtonToggle";

export default {
  components: {
    StatBox,
    GridPanel,
    CellRendererVerified,
    StatisticsCards,
    ButtonToggle,
    CellRendererDate,
  },
  mixins: [CellMatchingMixin, CanAccessMixin],
  data () {
    return {
      busy: true,
      statisticalData: {},
      searchQuery: '',
      showDeleted: false,
      componentAccess: {
        canCreate: {
          route: 'facilities.create',
          access: false,
        },
      },
      publishedFacilities: [],
      unpublishedFacilities: [],
      gridToggle: [
        {
          text: 'Published',
          value: 0,
        },
        {
          text: 'Unpublished',
          value: 1,
        }
      ],
      toggleValue: 0,

      // AgGrid
      columnDefs: [
        {
          headerName: 'Is Published',
          field: 'IsPublished',
          width: 350,
          cellRenderer: 'CellRendererVerified',
        },
        {
          headerName: 'Is Active',
          field: 'IsEnabled',
          width: 350,
          cellRenderer: 'CellRendererVerified',
        },
        {
          headerName: 'Facility Reference',
          field: 'FacilityReference',
          filter: 'agTextColumnFilter',
          width: 350,
        },
        {
          headerName: 'Class of Business',
          field: 'ClassOfBusiness',
          width: 350,
        },
        {
          headerName: 'Inception Date',
          field: 'InceptionDate',
          filter: 'agDateColumnFilter',
          width: 350,
          cellRenderer: "CellRendererDate",
          filterParams: dateFilterParams,
        },
        {
          headerName: 'Expiry Date',
          field: 'ExpiryDate',
          filter: 'agDateColumnFilter',
          width: 350,
          cellRenderer: "CellRendererDate",
          filterParams: dateFilterParams,
        },
        {
          headerName: 'Facility Type',
          field: 'FacilityType',
          filter: 'agTextColumnFilter',
          width: 350,
        },
        {
          headerName: 'Loss Adjuster',
          field: 'LossAdjuster',
          filter: 'agTextColumnFilter',
          width: 350,
        },
        {
          headerName: 'Risk Location',
          field: 'RiskLocation',
          filter: 'agTextColumnFilter',
          width: 350,
        },
        {
          headerName: 'Insured Domicile',
          field: 'InsuredDomicile',
          filter: 'agTextColumnFilter',
          width: 350,
        },
        {
          headerName: 'Territorial Limits',
          field: 'TerritorialLimits',
          filter: 'agTextColumnFilter',
          width: 350,
        },
        {
          headerName: 'Year of Account',
          field: 'YearOfAccount',
          width: 350,
        },
      ],
    }
  },
  computed: {
    showGridOne() {
      return this.toggleValue === 0;
    },
  },
  methods: {
    updateDeleted(newValue) {
      this.setFacilities({IsDeleted: newValue});
    },
    async facilities(params = {}) {
      const { data } = await this.$api.facilities.list({params});

      return data.data;
    },
    async setFacilities(params = {}) {
      this.busy = true;
      const allFacilities = await this.facilities(params);
      this.publishedFacilities = [];
      this.unpublishedFacilities = [];

      allFacilities.forEach((facility) => {
        if (facility.IsPublished) {
          this.publishedFacilities.push(facility);

          return;
        }

        this.unpublishedFacilities.push(facility);
      })
      this.busy = false;
    },
    async view({data}) {
      const route = 'facilities.view';

      if (await this.canAccessRoute(route)) {
        this.$router.push({ name: route, params: { id: data.id }}).catch(() => {});
      }
    },
    async fetchStats() {
      try {
        const { data } = await this.$api.facilities.counts();
        const localStatisticalData = {};
        Object.entries(data.data)
            .forEach(([key, value]) => {
              let icon = 'home';

              if (key === 'Facilities') {
                icon = 'folder-open';
              }

              if (key === 'Sections') {
                icon = 'bookmark';
              }

              if (key === 'Markets') {
                icon = 'analytics';
              }

              if (key === 'FDOs') {
                icon = 'chart-line';
              }

              return localStatisticalData[key] = {
                label: 'Total ' + key,
                value,
                tooltipText: key,
                icon
              };
            });

        this.statisticalData = localStatisticalData;
      } catch(e) {
        console.log(e);
      }
    }
  },
  mounted() {
    this.fetchStats();
    this.setFacilities({IsDeleted: 0});
  },
}

</script>

